import { RuleGroupType } from "../../../types/transactionRule";
import { Transaction } from "../../../types/transaction";
import {
    TransactionDto,
    TransactionDtoInput,
} from "../../transactions/transaction.dto";
import {
    BothModifiedConflict,
    BothModifiedConflictDto,
} from "./both-modified-conflict.dto";
import {
    MultipleMatchingConflict,
    MultipleMatchingConflictDto,
} from "./multiple-matching-conflict.dto";

export interface ReconciliationRange {
    openingDate: string;
    closingDate: string;
}

export interface ProtectedPeriodsWarning {
    hasProtectedData: boolean;
    lockDate: string | null;
    destinationReconciliationRanges: ReconciliationRange[];
    sourceReconciliationRanges: ReconciliationRange[];
}

export interface PausedRuleMergePreview {
    id: string;
    groupType: RuleGroupType;
}

export interface AutomaticRulesMergePreview {
    movedCount: number;
    pausedCount: number;
    pausedRules: PausedRuleMergePreview[];
}

export const EMPTY_AUTOMATIC_RULES_MERGE_PREVIEW: AutomaticRulesMergePreview = {
    movedCount: 0,
    pausedCount: 0,
    pausedRules: [],
};

export interface MergeFinancialAccountsResponse {
    wasDryRun: boolean;
    automaticRules: AutomaticRulesMergePreview;
    conflictWhenMerging?: ConflictWhenMerging;
    deduplicatedTransactions: Transaction[];
    movedTransactionsNumber: number;
    outOfWindowMovedTransactionsNumber: number;
    multipleMatchingConflicts: MultipleMatchingConflict[];
    bothModifiedConflicts: BothModifiedConflict[];
    dualReconciliationConflicts: BothModifiedConflict[];
    lockedNonDuplicateTransactions: Transaction[];
    lockedNonDuplicateTransactionsNumber: number;
    protectedPeriodsWarning: ProtectedPeriodsWarning;
}

export interface ConflictWhenMerging {
    bothModifiedConflict?: BothModifiedConflict;
    multipleMatchingConflict?: MultipleMatchingConflict;
}

interface ConflictWhenMergingDto {
    bothModifiedConflict?: BothModifiedConflictDto;
    multipleMatchingConflict?: MultipleMatchingConflictDto;
}

export class MergeFinancialAccountsResponseDto {
    wasDryRun: boolean;
    automaticRules: AutomaticRulesMergePreview;
    conflictWhenMerging?: ConflictWhenMergingDto;
    deduplicatedTransactions: TransactionDto[];
    movedTransactionsNumber: number;
    outOfWindowMovedTransactionsNumber: number;
    multipleMatchingConflicts: MultipleMatchingConflictDto[];
    bothModifiedConflicts: BothModifiedConflictDto[];
    dualReconciliationConflicts: BothModifiedConflictDto[];
    lockedNonDuplicateTransactions: TransactionDto[];
    lockedNonDuplicateTransactionsNumber: number;
    protectedPeriodsWarning: ProtectedPeriodsWarning;

    constructor(response: MergeFinancialAccountsResponse) {
        this.wasDryRun = response.wasDryRun;
        this.automaticRules = response.automaticRules;
        this.conflictWhenMerging = response.conflictWhenMerging
            ? {
                  bothModifiedConflict: response.conflictWhenMerging
                      .bothModifiedConflict
                      ? new BothModifiedConflictDto(
                            response.conflictWhenMerging.bothModifiedConflict,
                        )
                      : undefined,
                  multipleMatchingConflict: response.conflictWhenMerging
                      .multipleMatchingConflict
                      ? new MultipleMatchingConflictDto(
                            response.conflictWhenMerging
                                .multipleMatchingConflict,
                        )
                      : undefined,
              }
            : undefined;
        this.deduplicatedTransactions = response.deduplicatedTransactions.map(
            (t) => new TransactionDto(t as TransactionDtoInput),
        );
        this.movedTransactionsNumber = response.movedTransactionsNumber;
        this.outOfWindowMovedTransactionsNumber =
            response.outOfWindowMovedTransactionsNumber;
        this.multipleMatchingConflicts = response.multipleMatchingConflicts.map(
            (c) => new MultipleMatchingConflictDto(c),
        );
        this.bothModifiedConflicts = response.bothModifiedConflicts.map(
            (c) => new BothModifiedConflictDto(c),
        );
        this.dualReconciliationConflicts =
            response.dualReconciliationConflicts.map(
                (c) => new BothModifiedConflictDto(c),
            );
        this.lockedNonDuplicateTransactions =
            response.lockedNonDuplicateTransactions.map(
                (t) => new TransactionDto(t as TransactionDtoInput),
            );
        this.lockedNonDuplicateTransactionsNumber =
            response.lockedNonDuplicateTransactionsNumber;
        this.protectedPeriodsWarning = response.protectedPeriodsWarning;
    }

    static deserialize(
        dto: MergeFinancialAccountsResponseDto,
    ): MergeFinancialAccountsResponse {
        return {
            ...dto,
            conflictWhenMerging: dto.conflictWhenMerging
                ? {
                      bothModifiedConflict: dto.conflictWhenMerging
                          .bothModifiedConflict
                          ? BothModifiedConflictDto.deserialize(
                                dto.conflictWhenMerging.bothModifiedConflict,
                            )
                          : undefined,
                      multipleMatchingConflict: dto.conflictWhenMerging
                          .multipleMatchingConflict
                          ? MultipleMatchingConflictDto.deserialize(
                                dto.conflictWhenMerging
                                    .multipleMatchingConflict,
                            )
                          : undefined,
                  }
                : undefined,
            deduplicatedTransactions: dto.deduplicatedTransactions.map((t) =>
                TransactionDto.deserialize(t),
            ),
            multipleMatchingConflicts: dto.multipleMatchingConflicts.map((c) =>
                MultipleMatchingConflictDto.deserialize(c),
            ),
            bothModifiedConflicts: dto.bothModifiedConflicts.map((c) =>
                BothModifiedConflictDto.deserialize(c),
            ),
            dualReconciliationConflicts: dto.dualReconciliationConflicts.map(
                (c) => BothModifiedConflictDto.deserialize(c),
            ),
            lockedNonDuplicateTransactions:
                dto.lockedNonDuplicateTransactions.map((t) =>
                    TransactionDto.deserialize(t),
                ),
        };
    }
}
